import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useOrganizationFeaturesSettings,
  useOrganizationUpdateFeaturesSettings,
} from "@/queries/settings/use-organization.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiFeatureSettingsPayload,
  ApiFeatureType,
  ApiPlanFeatures,
} from "@/types/subscription.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Checkbox, Page, Skeleton, Stack, Title, useToasts } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { z } from "zod";

interface FeatureConfig {
  feature: keyof ApiPlanFeatures;
  title: string;
  description: string;
  dependsOn?: ApiFeatureType;
}

interface FeatureConfigGroup {
  title: string;
  features: FeatureConfig[];
}

export function FeaturesSettings() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { data: organizationFeatureSettings, isLoading } = useOrganizationFeaturesSettings();

  useTitle(t("core:features"), t("common:settings"));

  const featureGroups: FeatureConfigGroup[] = [
    {
      title: t("common:general"),
      features: [
        {
          feature: ApiFeatureType.core_company_calendar,
          title: t("core:core_company_calendar"),
          description: t("core:core_company_calendar_description"),
        },
      ],
    },
    {
      title: t("core:core_hr"),
      features: [
        {
          feature: ApiFeatureType.time_off,
          title: t("time_off:time_off"),
          description: t("time_off:time_off_description"),
        },
        {
          feature: ApiFeatureType.approvals,
          title: t("approval:approvals"),
          description: t("approval:approvals_description"),
        },
        {
          feature: ApiFeatureType.blog,
          title: t("core:news"),
          description: t("core:news_feature_description"),
        },
        {
          feature: ApiFeatureType.tasks,
          title: t("tasks:tasks"),
          description: t("tasks:tasks_feature_description"),
        },
      ],
    },
    {
      title: t("core:talent"),
      features: [
        {
          feature: ApiFeatureType.talent_surveys,
          title: t("surveys:surveys"),
          description: t("surveys:surveys_feature_description"),
        },
        {
          feature: ApiFeatureType.talent_surveys_ai,
          title: t("surveys:ai_feature_title"),
          description: t("surveys:ai_feature_description"),
          dependsOn: ApiFeatureType.talent_surveys,
        },
        {
          feature: ApiFeatureType.talent_performance_reviews,
          title: t("performance_review:performance_review"),
          description: t("performance_review:performance_reviews_feature_description"),
          dependsOn: ApiFeatureType.talent_performance_reviews,
        },
        {
          feature: ApiFeatureType.talent_performance_reviews_ai,
          title: t("performance_review:ai_feature_title"),
          description: t("performance_review:ai_feature_description"),
          dependsOn: ApiFeatureType.talent_performance_reviews,
        },
        {
          feature: ApiFeatureType.ai_assistant,
          title: t("ai:assistant_feature_title"),
          description: t("ai:assistant_feature_description"),
        },
        {
          feature: ApiFeatureType.recognitions,
          title: t("recognition:feature_title"),
          description: t("recognition:feature_description"),
        },
      ],
    },
    {
      title: t("ats:recruit"),
      features: [
        {
          feature: ApiFeatureType.ats,
          title: t("ats:recruit"),
          description: t("ats:ats_description"),
        },
        {
          feature: ApiFeatureType.ats_gdpr,
          title: t("data_protection:data_protection"),
          description: t("data_protection:settings_data_protection_description"),
        },
        {
          feature: ApiFeatureType.ats_hiring_plan,
          title: t("hiring_plan:hiring_plan"),
          description: t("hiring_plan:hiring_plan_description"),
        },
        {
          feature: ApiFeatureType.ats_ai,
          title: t("ats:ai_feature_title"),
          description: t("ats:use_ai_title"),
        },
      ],
    },
  ];

  const allFeatureTypes = featureGroups.flatMap((group) => group.features.map((f) => f.feature));
  const FeaturesSettingsSchemaObject = z.object(
    Object.fromEntries(
      allFeatureTypes.map((feature) => [feature, z.boolean().optional()])
    ) as Record<ApiFeatureType, z.ZodOptional<z.ZodBoolean>>
  );

  type FeaturesSettingsSchema = z.infer<typeof FeaturesSettingsSchemaObject>;

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FeaturesSettingsSchema>({
    mode: "onSubmit",
    resolver: zodResolver(FeaturesSettingsSchemaObject),
  });

  const availableFeatures = useMemo(() => {
    if (!organizationFeatureSettings) return [];

    const enabledMap: Record<ApiFeatureType, boolean> = Object.fromEntries(
      allFeatureTypes.map((key) => [key, false])
    ) as Record<ApiFeatureType, boolean>;

    const result: FeatureConfigGroup[] = [];

    for (const group of featureGroups) {
      const availableFeaturesInGroup: FeatureConfig[] = [];

      for (const feature of group.features) {
        const featureInfo = organizationFeatureSettings.plan.features[feature.feature];

        if (featureInfo?.available) {
          availableFeaturesInGroup.push(feature);
          enabledMap[feature.feature] = featureInfo?.enabled ?? false;
        }
      }

      if (availableFeaturesInGroup.length > 0) {
        result.push({
          title: group.title,
          features: availableFeaturesInGroup,
        });
      }
    }

    reset(enabledMap);
    return result;
  }, [organizationFeatureSettings]);

  const planFeaturesUpdate = useOrganizationUpdateFeaturesSettings(control);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.organization.settingsViewed, {
      [analyticProperties.source]: "Features",
    });
  });

  const onSubmit = handleSubmit(async (formData) => {
    if (!organizationFeatureSettings) return;

    const featuresPayload: ApiFeatureSettingsPayload["features"] = {};
    allFeatureTypes.forEach((key) => {
      featuresPayload[key] = { enabled: formData[key] || false };
    });

    try {
      await planFeaturesUpdate.mutateAsync({ features: featuresPayload });

      showInfo({
        title: t("common:you_changes_were_saved"),
        button: t("core:app_update_available_button"),
        onClick: () => {
          analyticsService.trackEvent(analyticEvents.pageActions.newVersionUpdateClicked);
          window.location.reload();
        },
      });

      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.actionType]: "Features Edited",
        [analyticProperties.source]: "Features",
      });
    } catch {
      return;
    }
  });
  return (
    <Page>
      <Page.Header showBack={true} navigateTo={"/settings/general"} title={t("core:features")} />
      <Page.Content>
        <Stack gap="xl" className="container-sm mx-auto">
          <form onSubmit={onSubmit}>
            <Stack>
              <Title
                size="lg"
                paddingBottom
                paddingTop
                header={t("core:settings_features")}
                caption={t("core:settings_features_description")}
              />

              <Stack gap="lg">
                {isLoading
                  ? [1, 2, 3, 4].map((i) => (
                      <Box key={i} variant="border">
                        <Stack gap="xs">
                          <Skeleton className="h-6 w-1/4" />
                          <Skeleton className="h-4 w-2/3" />
                        </Stack>
                      </Box>
                    ))
                  : availableFeatures.map((group) => (
                      <Stack key={group.title} gap="sm" className={"card-panel"}>
                        <Title header={group.title} size="md" paddingBottom={true} />
                        {group.features.map((feature) => (
                          <Box key={feature.feature}>
                            <Controller
                              name={feature.feature}
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  title={feature.title}
                                  caption={feature.description}
                                  checked={field.value}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </Box>
                        ))}
                      </Stack>
                    ))}
              </Stack>

              <ServerErrorField errors={errors} />

              <Box className="w-full flex flex-col pt-10">
                <Button type="submit" size="lg" isLoading={planFeaturesUpdate.isPending}>
                  {t("common:save")}
                </Button>
              </Box>
            </Stack>
          </form>
        </Stack>
      </Page.Content>
    </Page>
  );
}
